import React, { Component } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { PaymentRequestPendingReview } from "../../models/Orchestrator";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import { orchestratorApiService } from "../../services/api/Compliance/OrchestratorApiService";
import ReviewPendingPaymentRequestModal from "./ReviewPendingPaymentRequestModal";

interface State {
    loading: boolean;
    paymentRequests: PaymentRequestPendingReview[];
    totalRecords: number;
    selectedPaymentRequest: PaymentRequestPendingReview | null;
    loadError: string | null;
}

export default class ListPaymentRequestsOutgoing extends Component<Record<string, never>, State> {
    private dataTable: ExtendedDataTable | null = null;

    state: State = { loading: false, paymentRequests: [], totalRecords: 0, selectedPaymentRequest: null, loadError: null };

    private queueLoadFailureMessage = (status?: number) => {
        if (status === undefined) {
            return "Unable to reach the review queue. Check your connection and try again.";
        }

        if (status === 403) {
            return "You do not have permission to view the review queue.";
        }

        if (status === 408 || status === 504) {
            return "The review queue took too long to respond. Please try again.";
        }

        if (status >= 500) {
            return "The review queue is temporarily unavailable. Please try again later.";
        }

        return "The review queue could not be loaded. Please try again.";
    };

    loadData = async (request: SearchRequestBaseModel) => {
        this.setState({ loading: true, loadError: null });
        try {
            const result = await orchestratorApiService.getPendingPaymentReviews(request.pageNumber, request.pageSize);
            if (!result.success) {
                console.error("Unable to load pending Visa Direct reviews", result.message);
                this.setState({ loading: false, loadError: this.queueLoadFailureMessage(result.status) });
                return;
            }

            this.setState({ loading: false, paymentRequests: result.data, totalRecords: result.totalCount });
        } catch (error) {
            console.error("Unable to load pending Visa Direct reviews", error);
            this.setState({ loading: false, loadError: this.queueLoadFailureMessage() });
        }
    };

    closeReview = () => {
        this.setState({ selectedPaymentRequest: null });
        this.loadData({ pageNumber: this.dataTable?.state.page ?? 1, pageSize: this.dataTable?.state.pageRowCount ?? 10, filters: [], sortings: [] });
    };

    render() {
        return (
            <>
                {this.state.selectedPaymentRequest && <ReviewPendingPaymentRequestModal paymentRequest={this.state.selectedPaymentRequest} onHide={this.closeReview} />}
                {this.state.loadError && (
                    <div className="flex align-items-center justify-content-between gap-3 mb-3 p-3 border-round surface-100">
                        <span role="alert">{this.state.loadError}</span>
                        <Button
                            label="Retry"
                            icon="pi pi-refresh"
                            onClick={() => this.loadData({ pageNumber: this.dataTable?.state.page ?? 1, pageSize: this.dataTable?.state.pageRowCount ?? 10, filters: [], sortings: [] })}
                        />
                    </div>
                )}
                <ExtendedDataTable
                    value={this.state.paymentRequests}
                    totalRecords={this.state.totalRecords}
                    onChange={this.loadData}
                    loading={this.state.loading}
                    ref={(ref) => (this.dataTable = ref)}
                    defaultSortOrder={SortOrderType.Descending}
                    defaultSortField="createdAtUtc"
                >
                    <Column header="Created" body={(row: PaymentRequestPendingReview) => new Date(row.createdAtUtc).toLocaleString()} />
                    <Column header="Customer" field="senderName" />
                    <Column header="Beneficiary" field="beneficiaryName" />
                    <Column header="Amount" body={(row: PaymentRequestPendingReview) => `${row.amount} ${row.currency}`} />
                    <Column header="Route" field="routeId" />
                    <Column header="Reason" body={(row: PaymentRequestPendingReview) => [row.riskReasonCode, row.riskReasonText].filter(Boolean).join(" — ")} />
                    <Column header="Actions" body={(row: PaymentRequestPendingReview) => <Button label="Review" severity="warning" onClick={() => this.setState({ selectedPaymentRequest: row })} />} />
                </ExtendedDataTable>
            </>
        );
    }
}
